import { IGridCage, IGridEdgeLine, IGridSide } from 'src/generated-types/grid-expansion'
import { addedCells, gridBounds } from './expandGrid.action'

// The project drawing's own units (MapSvgGenerator): cages are 160 apart, the first corner sits at 20, and a corner is a
// 31 x 22 box whose centre is where a mooring line starts.
export const CAGE_SIZE = 160
const ORIGIN = 20
export const CORNER_SIZE = { width: 31, height: 22 }
// Room kept round the grid for the mooring lines (120 long), their names and the side arrows.
const MARGIN = 310
const ARROW_DISTANCE = 250
// The old lines stay in the drawing, faded, so the moved ones can be told from them.
const FADED_OPACITY = 0.3

export interface Point {
  x: number
  y: number
}

export interface Rect extends Point {
  width: number
  height: number
}

export interface CanvasLayout {
  viewBox: string
  // The cells the expansion adds, and the corners it adds, in the drawing's units.
  cells: Rect[]
  corners: Point[]
  arrows: Record<IGridSide, Point>
  // How far the expansion carries a line from the corner it leaves to the one it goes to.
  shiftOf: (line: IGridEdgeLine) => Point
}

const cornersOf = (cells: { column: number; row: number }[]) =>
  new Map(
    cells.flatMap(({ column, row }) =>
      [
        [column, row],
        [column + 1, row],
        [column, row + 1],
        [column + 1, row + 1],
      ].map(([c, r]) => [`${c}:${r}`, { column: c, row: r }] as const),
    ),
  )

// Lays what a side adds over the drawing the backend makes of the grid as it is now: columns run left to right, rows
// bottom to top. A cage sits between its lowest corner and the next column and row.
export const canvasLayout = (cages: IGridCage[], side: IGridSide, count: number): CanvasLayout => {
  const added = addedCells(cages, side, count)
  const own = gridBounds(cages)
  const all = gridBounds([...cages, ...added])
  const x = (column: number) => ORIGIN + CAGE_SIZE * (column - own.minColumn) + CORNER_SIZE.width / 2
  const y = (row: number) => ORIGIN + CAGE_SIZE * (own.maxRow + 1 - row) + CORNER_SIZE.height / 2

  const left = x(all.minColumn)
  const right = x(all.maxColumn + 1)
  const top = y(all.maxRow + 1)
  const bottom = y(all.minRow)
  const existing = cornersOf(cages)
  const newCorners = [...cornersOf([...cages, ...added])].filter(([key]) => !existing.has(key)).map(([, c]) => c)

  return {
    viewBox: `${left - MARGIN} ${top - MARGIN} ${right - left + 2 * MARGIN} ${bottom - top + 2 * MARGIN}`,
    cells: added.map(cell => ({
      x: x(cell.column),
      y: y(cell.row + 1),
      width: x(cell.column + 1) - x(cell.column),
      height: y(cell.row) - y(cell.row + 1),
    })),
    corners: newCorners.map(c => ({ x: x(c.column), y: y(c.row) })),
    arrows: {
      [IGridSide.Left]: { x: left - ARROW_DISTANCE, y: (top + bottom) / 2 },
      [IGridSide.Right]: { x: right + ARROW_DISTANCE, y: (top + bottom) / 2 },
      [IGridSide.Top]: { x: (left + right) / 2, y: top - ARROW_DISTANCE },
      [IGridSide.Bottom]: { x: (left + right) / 2, y: bottom + ARROW_DISTANCE },
    },
    shiftOf: line => ({
      x: (line.toColumn - line.fromColumn) * CAGE_SIZE,
      y: -(line.toRow - line.fromRow) * CAGE_SIZE,
    }),
  }
}

export type Box = [number, number, number, number]

export const parseBox = (viewBox: string) => viewBox.split(' ').map(Number) as Box

// The part of the preview to show while a line is hovered: its old and new place, with room for the names, and never
// closer than a third of the whole preview.
export const zoomBox = (whole: Box, points: Point[]): Box => {
  const xs = points.map(p => p.x)
  const ys = points.map(p => p.y)
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)]
  const aspect = whole[2] / whole[3]
  let width = Math.max(maxX - minX + 2 * ZOOM_PADDING.x, whole[2] / 3)
  let height = width / aspect
  if (maxY - minY + 2 * ZOOM_PADDING.y > height) {
    height = maxY - minY + 2 * ZOOM_PADDING.y
    width = height * aspect
  }
  width = Math.min(width, whole[2])
  height = Math.min(height, whole[3])
  return [(minX + maxX) / 2 - width / 2, (minY + maxY) / 2 - height / 2, width, height]
}

const ZOOM_PADDING = { x: 190, y: 120 }

export type LabelKind = 'moved' | 'removed' | 'new'

export interface LabelSpec {
  key: string
  lineId: number
  kind: LabelKind
  text: string
  // The anchor the name belongs to, which a leader joins it to when the name had to move away.
  from: Point
  x: number
  y: number
  textAnchor: 'start' | 'middle' | 'end'
}

export interface PlacedLabel extends LabelSpec {
  displaced: boolean
}

const CHAR_WIDTH = 10.5
const LABEL_STEP = 22
const LABEL_PAD = 3

// Names sit just past the end of a line, on the side it points to; a replaced line's old name sits above its cross
// (beside it on the top and bottom), so it does not run into the new line.
export const baseLabel = (anchor: Point, side: IGridSide, kind: LabelKind) => {
  const sideways = side === IGridSide.Left || side === IGridSide.Right
  if (kind === 'removed') {
    return sideways
      ? { x: anchor.x, y: anchor.y - 26, textAnchor: 'middle' as const }
      : { x: anchor.x - 18, y: anchor.y + 5, textAnchor: 'end' as const }
  }
  if (side === IGridSide.Right) return { x: anchor.x + 16, y: anchor.y + 5, textAnchor: 'start' as const }
  if (side === IGridSide.Left) return { x: anchor.x - 16, y: anchor.y + 5, textAnchor: 'end' as const }
  return { x: anchor.x, y: anchor.y + (side === IGridSide.Top ? -16 : 26), textAnchor: 'middle' as const }
}

const boxOf = (label: LabelSpec) => {
  const width = label.text.length * CHAR_WIDTH
  const x0 = label.textAnchor === 'start' ? label.x : label.textAnchor === 'end' ? label.x - width : label.x - width / 2
  return { x0, x1: x0 + width, y0: label.y - 15, y1: label.y + 5 }
}

const overlaps = (a: LabelSpec, b: LabelSpec) => {
  const [p, q] = [boxOf(a), boxOf(b)]
  return p.x0 < q.x1 + LABEL_PAD && q.x0 < p.x1 + LABEL_PAD && p.y0 < q.y1 + LABEL_PAD && q.y0 < p.y1 + LABEL_PAD
}

// Moves names apart where they would overlap: up and down beside a left or right side, further out above or below a top
// or bottom side.
export const placeLabels = (labels: LabelSpec[], side: IGridSide): PlacedLabel[] => {
  const sideways = side === IGridSide.Left || side === IGridSide.Right
  const outward = side === IGridSide.Top ? -1 : 1
  const offsets = sideways
    ? [0, 1, -1, 2, -2, 3, -3, 4, -4].map(k => k * LABEL_STEP)
    : [0, 1, 2, 3, 4, 5, 6].map(k => k * LABEL_STEP * outward)
  const placed: PlacedLabel[] = []
  ;[...labels]
    .sort((a, b) => (sideways ? a.y - b.y : a.x - b.x) || a.key.localeCompare(b.key))
    .forEach(label => {
      const moved = offsets
        .map(offset => ({ ...label, y: label.y + offset, displaced: offset !== 0 }))
        .find(candidate => !placed.some(other => overlaps(candidate, other)))
      placed.push(moved ?? { ...label, displaced: false })
    })
  return placed
}

export interface LineShape {
  start: Point
  anchor: Point
}

const shapeOf = (element: Element): LineShape | undefined => {
  if (element.tagName.toLowerCase() === 'line') {
    const [x1, y1, x2, y2] = ['x1', 'y1', 'x2', 'y2'].map(name => Number(element.getAttribute(name)))
    return { start: { x: x1, y: y1 }, anchor: { x: x2, y: y2 } }
  }
  // A line from two corners is a Y: corner, joint, corner, then the stem from the joint to the anchor.
  const numbers = (element.getAttribute('d')?.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number)
  const points = Array.from({ length: Math.floor(numbers.length / 2) }, (_, i) => ({
    x: numbers[2 * i],
    y: numbers[2 * i + 1],
  }))
  if (points.length < 2) return undefined
  return { start: points.length >= 5 ? points[1] : points[0], anchor: points[points.length - 1] }
}

export interface DrawingParts {
  // The drawing without its own svg element, so it can sit inside one that has the preview's viewBox.
  content: string
  shapes: Map<number, LineShape>
}

// Reads where each of the lines the expansion touches is in the drawing, and fades them there.
export const readDrawing = (svg: string | undefined, lines: { id: number; name?: string }[]): DrawingParts => {
  const parts: DrawingParts = { content: '', shapes: new Map() }
  if (!svg) return parts
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml')
  const root = doc.documentElement
  if (root.localName !== 'svg') return parts

  lines.forEach(line => {
    const element = doc.querySelector(`[id="positionId-${line.id}"]`)
    if (!element) return
    const shape = shapeOf(element)
    if (shape) parts.shapes.set(line.id, shape)
    const anchor = line.name ? doc.querySelector(`[id="${line.name}"]`) : null
    // The anchor's symbol follows its circle without an id of its own.
    ;[element, anchor, anchor?.nextElementSibling, doc.querySelector(`[id="positionTextId-${line.id}"]`)].forEach(
      node => node?.setAttribute('opacity', String(FADED_OPACITY)),
    )
  })
  const serializer = new XMLSerializer()
  parts.content = Array.from(root.childNodes)
    .map(node => serializer.serializeToString(node))
    .join('')
  return parts
}
